import { observer } from 'mobx-react-lite';
import { jumpToMeetingInsightsButton } from './MeetingInsightsButton.locstring.json';
import loc from 'owa-localize';
import { setHasScrolledInDetailsForm } from '../actions/publicActions';
import {
    logMeetingInsightsButtonClicked,
    logJumpToMeetingInsightsButtonShown,
} from '../helpers/instrumentationHelpers';
import { getInsightsTraceId, getRelatedFilesAndEmailsCount } from 'owa-meeting-insights';
import type { InsightsDataFetchSource } from 'owa-meeting-insights-types-and-flights';
import { DefaultButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import ColorScheme, { colorSchemeToCss } from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import * as React from 'react';

const styles = require('./MeetingInsightsButton.scss');

export interface MeetingInsightsButtonProps {
    colorScheme: ColorScheme;
    meetingId: string;
    relatedContentRef: HTMLElement;
    insightsEntrySource: InsightsDataFetchSource;
}

const MeetingInsightsButton: React.FunctionComponent<MeetingInsightsButtonProps> = props => {
    const { colorScheme, meetingId, relatedContentRef, insightsEntrySource } = props;

    React.useEffect(() => {
        // Log datapoint that meeting insights button is shown
        logJumpToMeetingInsightsButtonShown();
    }, []);

    const onMeetingInsightsButtonClicked = React.useCallback(() => {
        // scroll to related content
        relatedContentRef.scrollIntoView({
            behavior: 'smooth',
        });
        setHasScrolledInDetailsForm(true);
        logMeetingInsightsButtonClicked(getInsightsTraceId(meetingId, insightsEntrySource));
    }, [relatedContentRef, meetingId, insightsEntrySource]);

    if (!relatedContentRef) {
        return null;
    }

    const calendarColorStyle = colorSchemeToCss(colorScheme);

    return (
        <div className={styles.container}>
            <DefaultButton
                className={styles.insightsButton}
                onClick={onMeetingInsightsButtonClicked}>
                <Icon
                    iconName={ControlIcons.DoubleChevronDown}
                    className={styles.doubleChevronDownIcon}
                    styles={{
                        root: {
                            color: colorScheme.calDarkAlt,
                        },
                    }}
                />
                {loc(jumpToMeetingInsightsButton)}
                <div
                    className={styles.badgeContainer}
                    style={{
                        backgroundColor: calendarColorStyle.backgroundColor,
                        color: calendarColorStyle.color,
                    }}>
                    <p className={styles.count}>
                        {getRelatedFilesAndEmailsCount(meetingId, insightsEntrySource)}
                    </p>
                </div>
            </DefaultButton>
        </div>
    );
};

export default observer(MeetingInsightsButton);
